// apps/web/src/services/http.ts
export const BASE_URL = import.meta.env.VITE_API_URL ?? 'http://localhost:3000/api'
import { z } from 'zod'
import Sentry from '@/lib/sentry'
import { connectivity } from '@/services/connectivity'

export type HttpMethod = 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE'

// Version tracking for forced logout on API updates
const VERSION_HEADER = 'x-app-version'
const VERSION_STORAGE_KEY = 'app-api-version'
let isReloading = false

/**
 * Get the stored API version from localStorage.
 * Returns null if not stored or localStorage is unavailable.
 */
function getStoredVersion(): string | null {
	try {
		return localStorage.getItem(VERSION_STORAGE_KEY)
	} catch {
		return null
	}
}

/**
 * Store the API version in localStorage.
 */
function setStoredVersion(version: string): void {
	try {
		localStorage.setItem(VERSION_STORAGE_KEY, version)
	} catch {}
}

/**
 * Check if the user appears to be logged in (has user data in localStorage).
 */
function hasStoredUser(): boolean {
	try {
		return localStorage.getItem('user') !== null
	} catch {
		return false
	}
}

/**
 * Force logout by clearing both localStorage AND the backend session cookie,
 * then reload the page. This ensures users can't auto-login via cookie after reload.
 */
function forceLogoutAndReload(serverVersion: string, reason: string): void {
	if (isReloading) return
	isReloading = true

	// eslint-disable-next-line no-console
	console.warn(`[api] ${reason}. Forcing logout...`)

	try {
		Sentry.addBreadcrumb({
			category: 'version',
			level: 'warning',
			message: reason,
			data: { serverVersion },
		})
	} catch {}

	// Store the new version BEFORE logout to prevent loops
	setStoredVersion(serverVersion)

	// Clear localStorage auth state
	try {
		localStorage.removeItem('user')
	} catch {}

	// Call the logout endpoint to clear the backend session cookie.
	// Use raw fetch to avoid recursion through checkVersionMismatch.
	// Fire-and-forget: we reload regardless of success/failure.
	// Note: Don't set Content-Type header since we're not sending a body -
	// Fastify will try to parse an empty JSON body and return 400.
	fetch(`${BASE_URL}/auth/logout`, {
		method: 'POST',
		credentials: 'include',
	})
		.catch(() => {
			// Ignore errors - we'll reload anyway
		})
		.finally(() => {
			window.location.reload()
		})
}

/**
 * Decide what to do when an API response advertises a version.
 *
 * No stored version is either:
 * 1. First time user (no session) - just store and continue
 * 2. Returning user from before this feature was deployed - force logout
 *    We detect case 2 by checking if there's a stored user.
 *
 * A stored version that does not match the server:
 * - Logged-in users still get deploy-time session invalidation
 *   (and APPLICATIONS_CLOSED kick-out).
 * - Anonymous visitors (including mid-OTP login) only persist the new
 *   version. Reloading them would send the user back to the email form
 *   after Twilio had already sent a code.
 */
export function resolveVersionAction(
	serverVersion: string | null,
	storedVersion: string | null,
	hasUser: boolean
): 'ignore' | 'store' | 'logout' {
	if (!serverVersion) return 'ignore'
	if (storedVersion === serverVersion) return 'ignore'
	if (!hasUser) return 'store'
	return 'logout'
}

/**
 * Check the API version header and force logout + reload if it changed.
 * The version is persisted in localStorage so that even opening a new tab
 * after a deploy will detect the mismatch and force logout.
 *
 * This invalidates existing sessions after each deploy, similar to rotating
 * a JWT secret. It must not interrupt unauthenticated flows such as OTP start.
 */
function checkVersionMismatch(res: Response): boolean {
	if (isReloading) return true // already handling a reload

	const serverVersion = res.headers.get(VERSION_HEADER)
	const storedVersion = getStoredVersion()
	const action = resolveVersionAction(serverVersion, storedVersion, hasStoredUser())

	if (action === 'ignore' || !serverVersion) return false

	if (action === 'store') {
		setStoredVersion(serverVersion)
		return false
	}

	const reason =
		storedVersion === null
			? 'No stored version with existing user'
			: `Version mismatch (stored: ${storedVersion}, server: ${serverVersion})`
	forceLogoutAndReload(serverVersion, reason)
	return true
}

export class HttpError extends Error {
	status: number
	statusText: string
	body: unknown
	/**
	 * True when the response did not come from our API but from the layer in
	 * front of it (Heroku router / Cloudflare 502-504 while a dyno restarts).
	 * Such responses carry no `x-app-version` header.
	 */
	unreachable: boolean
	constructor(
		message: string,
		status: number,
		statusText: string,
		body: unknown,
		unreachable = false
	) {
		super(message)
		this.status = status
		this.statusText = statusText
		this.body = body
		this.unreachable = unreachable
	}
}

const GATEWAY_STATUSES = new Set([502, 503, 504])

/** A gateway error page rather than an answer from our API. */
function isGatewayResponse(res: Response): boolean {
	return GATEWAY_STATUSES.has(res.status) && !res.headers.get(VERSION_HEADER)
}

/**
 * True when the request never reached the API: fetch rejected (offline, DNS,
 * CORS, connection refused while the API restarts) or a gateway answered for it.
 */
export function isUnreachableError(e: unknown): boolean {
	if (e instanceof HttpError) return e.unreachable
	return e instanceof TypeError
}

function join(base: string, path: string) {
	if (!base.endsWith('/') && !path.startsWith('/')) return `${base}/${path}`
	if (base.endsWith('/') && path.startsWith('/')) return `${base}${path.slice(1)}`
	return `${base}${path}`
}

import type { ZodType } from 'zod'

type ApiInit = Omit<RequestInit, 'method'> & {
	method?: HttpMethod
	/**
	 * Error statuses that are normal outcomes for this call (e.g. a wrong OTP code).
	 * They still throw HttpError and leave a breadcrumb, but are not reported as Sentry issues.
	 */
	expectedStatuses?: readonly number[]
}

type ErrorShape = { error?: unknown; message?: unknown }
const isRecord = (v: unknown): v is Record<string, unknown> =>
	typeof v === 'object' && v !== null && !Array.isArray(v)

export async function api<T = unknown>(
	path: string,
	{ expectedStatuses, ...opts }: ApiInit = {},
	schema?: ZodType<T>
): Promise<T> {
	const url = join(BASE_URL, path)

	// Attach a client-generated request id to help correlate client↔server logs
	const reqId = ((globalThis as any)?.crypto?.randomUUID?.() ??
		Math.random().toString(36).slice(2)) as string

	try {
		// Intentionally no breadcrumb for request start to avoid noise on success

		const started = performance.now?.() ?? Date.now()
		let res: Response
		try {
			res = await fetch(url, {
				credentials: 'include',
				headers: {
					'Content-Type': 'application/json',
					'x-request-id': reqId,
					'x-app-client': 'web',
					...(opts.headers ?? {}),
				},
				...opts,
			})
		} catch (e) {
			// fetch itself rejected: the API was never reached.
			connectivity.reportFailure()
			throw e
		}

		const serverRid = res.headers.get('x-request-id') || undefined
		const duration = (performance.now?.() ?? Date.now()) - started

		const unreachable = isGatewayResponse(res)
		if (unreachable) connectivity.reportFailure()
		else connectivity.reportSuccess()

		// Check for version mismatch on every response (including errors)
		if (checkVersionMismatch(res)) {
			// Do not let stale response data render while the logout request is
			// clearing the session. The pending navigation is replaced by the
			// reload started in forceLogoutAndReload.
			return new Promise<never>(() => {})
		}

		// Handle 401 Unauthorized - clear auth state and redirect to login
		if (res.status === 401) {
			// eslint-disable-next-line no-console
			console.warn('[api] 401 Unauthorized - clearing auth and redirecting to login')
			try {
				Sentry.addBreadcrumb({
					category: 'auth',
					level: 'warning',
					message: '401 Unauthorized - forcing logout',
					data: { url, request_id: reqId },
				})
			} catch {}

			// Clear auth state
			try {
				localStorage.removeItem('user')
			} catch {}

			// Redirect to login
			try {
				window.location.replace('/login')
			} catch {}

			// Throw error so the calling code knows the request failed
			throw new HttpError('Unauthorized', 401, 'Unauthorized', null)
		}

		if (!res.ok) {
			try {
				Sentry.addBreadcrumb({
					category: 'api',
					level: 'error',
					data: {
						method: opts.method ?? 'GET',
						url,
						status: res.status,
						duration_ms: Math.round(duration),
						request_id: reqId,
						server_request_id: serverRid,
					},
				})
			} catch {}
		}

		if (!res.ok) {
			let body: unknown = null
			const ct = res.headers.get('content-type') ?? ''
			try {
				body = ct.includes('application/json') ? await res.json() : await res.text()
			} catch {
				body = null
			}

			let msg = `${res.status} ${res.statusText}`
			if (typeof body === 'string' && body.trim()) {
				msg = body
			} else if (isRecord(body)) {
				const b = body as ErrorShape
				if (typeof b.error === 'string' && b.error?.toString().trim()) msg = b.error as string
				else if (typeof b.message === 'string' && b.message?.toString().trim())
					msg = b.message as string
			}

			// Include correlation in Sentry for easier backtracking
			if (!expectedStatuses?.includes(res.status)) {
				try {
					Sentry.captureException(new Error(`HTTP ${res.status} ${res.statusText}`), {
						extra: { path, url, request_id: reqId, server_request_id: serverRid, body },
						level: 'error',
					})
				} catch {}
			}

			throw new HttpError(msg, res.status, res.statusText, body, unreachable)
		}

		if (res.status === 204) return undefined as unknown as T
		const contentType = res.headers.get('content-type') ?? ''
		if (!contentType.includes('application/json')) {
			const text = await res.text().catch(() => '')
			return text as unknown as T
		}
		const parsedJson = (await res.json()) as unknown

		// If the API uses the standardized envelope { message, status, code?, data },
		// unwrap `.data` before validating with the provided Zod schema. If no schema
		// is given, return the raw parsed JSON (preserves backwards compatibility).
		let valueToValidate: unknown = parsedJson
		if (
			isRecord(parsedJson) &&
			'data' in parsedJson &&
			('message' in parsedJson || 'status' in parsedJson || 'code' in parsedJson)
		) {
			// safe to index as a record here
			valueToValidate = (parsedJson as Record<string, unknown>).data
		}

		// If a Zod schema is provided, validate the (possibly unwrapped) response and throw with details on failure
		if (schema) {
			const result = schema.safeParse(valueToValidate)
			if (!result.success) {
				// include formatted zod error for easier debugging
				const details = z.treeifyError(result.error)
				try {
					Sentry.captureException(new Error('Response validation failed'), {
						extra: {
							validation: details,
							value: valueToValidate,
							path,
							request_id: reqId,
							server_request_id: serverRid,
						},
					})
				} catch {
					// don't let Sentry failures block app behavior
				}
				throw new Error(`Response validation failed: ${JSON.stringify(details)}`)
			}
			return result.data as T
		}

		return parsedJson as T
	} catch (e) {
		// Add a breadcrumb for network errors (e.g., CORS, offline)
		try {
			Sentry.addBreadcrumb({
				category: 'api',
				level: 'error',
				data: { url, request_id: reqId, error: (e as Error)?.message },
			})
		} catch {}
		throw e
	}
}

export async function get<T = unknown>(path: string, schema?: ZodType<T>): Promise<T> {
	return api<T>(path, { method: 'GET' }, schema)
}

export async function post<T = unknown>(
	path: string,
	body?: unknown,
	schema?: ZodType<T>,
	init?: Pick<ApiInit, 'expectedStatuses'>
): Promise<T> {
	return api<T>(
		path,
		{ ...init, method: 'POST', body: body == null ? undefined : JSON.stringify(body) },
		schema
	)
}

export async function put<T = unknown>(
	path: string,
	body?: unknown,
	schema?: ZodType<T>
): Promise<T> {
	return api<T>(
		path,
		{ method: 'PUT', body: body == null ? undefined : JSON.stringify(body) },
		schema
	)
}

export async function patch<T = unknown>(
	path: string,
	body: unknown,
	schema?: ZodType<T>
): Promise<T> {
	return api<T>(path, { method: 'PATCH', body: JSON.stringify(body) }, schema)
}

export async function del<T = unknown>(path: string, schema?: ZodType<T>): Promise<T> {
	return api<T>(path, { method: 'DELETE' }, schema)
}
